import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { FiLink, FiCheckCircle, FiLock } from 'react-icons/fi';
import { authAPI } from '../services/api';
import { ROUTES } from '../constants/constants';
import Button from '../components/common/Button';

/* ── Tiny reusable Input (mirrors LoginForm's Input) ── */
function Input({ label, ...props }) {
  return (
    <div className="mb-[14px]">
      <div className="text-[11px] font-semibold uppercase tracking-[.8px] text-[#7a7060] mb-[5px]">
        {label}
      </div>
      <input
        {...props}
        className="w-full border-[1.5px] rounded-[9px] px-3 py-[10px] text-[12.5px] outline-none"
        style={{ borderColor: '#ddd6c6' }}
      />
    </div>
  );
}

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [busy, setBusy] = useState(false);
  const [redirectCount, setRedirectCount] = useState(3);

  // Redirect countdown after success
  useEffect(() => {
    if (!success) return;
    if (redirectCount <= 0) { navigate(ROUTES.LOGIN, { replace: true }); return; }
    const t = setTimeout(() => setRedirectCount((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [success, redirectCount, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!token) { setError('Reset token is missing. Please use the link from your email.'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirmPassword) { setError('Passwords do not match.'); return; }

    // Check password strength
    const strengthRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>])/;
    if (!strengthRegex.test(password)) {
      setError('Password must include uppercase, lowercase, number, and special character.');
      return;
    }

    setBusy(true);
    try {
      const result = await authAPI.resetPassword(token, password);
      if (result?.success) {
        setSuccess(result.message || 'Password reset successfully!');
      } else {
        setError(result?.message || 'Failed to reset password.');
      }
    } catch (err) {
      setError(err?.message || 'Invalid or expired reset link. Please request a new one.');
    } finally {
      setBusy(false);
    }
  };

  /* ── No token in URL ── */
  if (!token) {
    return (
      <div className="h-[var(--app-vh)] w-full flex overflow-hidden" style={{ background: '#f4f0e8' }}>
        {/* Left decorative panel */}
        <div className="relative hidden lg:flex h-[var(--app-vh)] flex-1 px-[52px] py-12 flex-col justify-center items-center overflow-hidden"
          style={{ background: 'linear-gradient(135deg,#0a1628 0%,#0d2a3a 40%,#0a3d2a 100%)' }}>
          <div style={{ fontFamily: "'Cormorant Garamond', serif" }}
            className="text-[32px] font-bold text-[#b8973a] text-center">
            ComplyV
          </div>
          <div className="text-[13px] text-white/50 mt-2 text-center">
            Corporate Governance & Compliance Platform
          </div>
        </div>

        {/* Right panel */}
        <div className="w-full lg:w-[460px] h-[var(--app-vh)] bg-[#f4f0e8] overflow-y-auto px-7 md:px-12 py-10 md:py-[52px] flex items-center">
          <div className="w-full max-w-[364px] mx-auto text-center">
            <div className="flex justify-center mb-3"><FiLink size={40} style={{ color: '#145f5f' }} /></div>
            <div className="text-[20px] font-bold text-[#08111f] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Invalid Reset Link
            </div>
            <div className="text-[13px] text-[#7a7060] mb-6 leading-relaxed">
              This password reset link is invalid or missing. Please go back to the login page and request a new reset link.
            </div>
            <Button block size="lg" onClick={() => navigate(ROUTES.LOGIN)}>
              Go to Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  /* ── Main reset form ── */
  return (
    <div className="h-[var(--app-vh)] w-full flex overflow-hidden" style={{ background: '#f4f0e8' }}>
      {/* Left decorative panel */}
      <div className="relative hidden lg:flex h-[var(--app-vh)] flex-1 px-[52px] py-12 flex-col justify-center items-center overflow-hidden"
        style={{ background: 'linear-gradient(135deg,#0a1628 0%,#0d2a3a 40%,#0a3d2a 100%)' }}>
        <div style={{ fontFamily: "'Cormorant Garamond', serif" }}
          className="text-[32px] font-bold text-[#b8973a] text-center">
          ComplyV
        </div>
        <div className="text-[13px] text-white/50 mt-2 text-center">
          Corporate Governance & Compliance Platform
        </div>
      </div>

      {/* Right panel — form */}
      <div className="w-full lg:w-[460px] h-[var(--app-vh)] bg-[#f4f0e8] overflow-y-auto px-7 md:px-12 py-10 md:py-[52px] flex items-center">
        <div className="w-full max-w-[364px] mx-auto">

          {/* ── Success state ── */}
          {success ? (
            <div className="text-center">
              <div className="flex justify-center mb-3"><FiCheckCircle size={40} style={{ color: '#4a6b3f' }} /></div>
              <div className="text-[20px] font-bold text-[#08111f] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Password Reset!
              </div>
              <div className="mb-4 text-[12.5px] px-3 py-2 rounded-lg border bg-[#e8efe5] border-[#d3e2cb] text-[#4a6b3f]">
                {success}
              </div>
              <div className="text-[12.5px] text-[#7a7060]">
                Redirecting to login in {redirectCount}s...
              </div>
              <Button variant="outline" block className="mt-4" onClick={() => navigate(ROUTES.LOGIN, { replace: true })}>
                Go to Login Now
              </Button>
            </div>
          ) : (
            /* ── Form state ── */
            <form onSubmit={handleSubmit}>
              <div className="text-center mb-5">
                <div className="flex justify-center mb-2"><FiLock size={40} style={{ color: '#145f5f' }} /></div>
                <div className="text-[26px] font-bold mb-[6px]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  Set New Password
                </div>
                <div className="text-[12.5px] text-[#7a7060]">
                  Choose a strong password for your account
                </div>
              </div>

              {error && (
                <div className="mb-3 text-[12.5px] px-3 py-2 rounded-lg border bg-[#f2e9e6] border-[#e6d2cb] text-[#9a564a]">
                  {error}
                </div>
              )}

              <div className="mb-[14px]">
                <div className="text-[11px] font-semibold uppercase tracking-[.8px] text-[#7a7060] mb-[5px]">New Password</div>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Min. 8 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    autoFocus
                    className="w-full border-[1.5px] rounded-[9px] px-3 py-[10px] text-[12.5px] outline-none pr-10"
                    style={{ borderColor: '#ddd6c6' }}
                  />
                  <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-[14px] opacity-50 hover:opacity-100" onClick={() => setShowPassword((p) => !p)}>&#128065;</button>
                </div>
              </div>

              <div className="mb-[14px]">
                <div className="text-[11px] font-semibold uppercase tracking-[.8px] text-[#7a7060] mb-[5px]">Confirm Password</div>
                <div className="relative">
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    placeholder="Re-enter your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={8}
                    className="w-full border-[1.5px] rounded-[9px] px-3 py-[10px] text-[12.5px] outline-none pr-10"
                    style={{ borderColor: '#ddd6c6' }}
                  />
                  <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-[14px] opacity-50 hover:opacity-100" onClick={() => setShowConfirm((p) => !p)}>&#128065;</button>
                </div>
              </div>

              {/* Password requirements hint */}
              <div className="text-[10.5px] text-[#7a7060] mb-4 leading-relaxed">
                Must include: uppercase, lowercase, number, and special character (!@#$%...)
              </div>

              <Button type="submit" block size="lg" loading={busy}>
                {busy ? 'Resetting...' : 'Reset Password'}
              </Button>

              <div className="text-center mt-4">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(ROUTES.LOGIN)}>
                  &larr; Back to Login
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
